import { REPRICE_DAYS_TO_SUM } from '@/configs/settings';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { useCallback, useMemo, useState } from 'react';

export type ReceivingFilters = {
  new: boolean;
  partial: boolean;
  inUse: boolean;
};

enum ReceivingMode {
  DATES = 'DATES',
  DELIVERY = 'DELIVERY',
}

type RepricingPageState = {
  filters: ReceivingFilters;
  startDate: Date | null;
  endDate: Date | null;
  deliveryNumber: string;
  mode: ReceivingMode | null;
  // hasActiveFilters: boolean;
};

type RepricingPageActions = {
  resetFilters: () => void;
  toggleFilter: (key: keyof ReceivingFilters) => void;
  activateDateMode: () => void;
  activateDeliveryMode: () => void;
  clearMode: () => void;
  resetDates: () => void;
  resetDelivery: () => void;
  setDeliverySearch: (value: string) => void;
  setStartDateOnly: (date: Date | null) => void;
  setEndDateOnly: (date: Date | null) => void;
  getInitialDates: () => {start: Date; end: Date};
};

type RepricingPage = {
  state: RepricingPageState;
  actions: RepricingPageActions;
};

export const useReceivingPage = (): RepricingPage => {
  const [filters, setFilters] = useState<ReceivingFilters>({
    new: true,
    partial: true,
    inUse: true,
  });

  const initialDates = useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() - REPRICE_DAYS_TO_SUM);

    const end = new Date();

    return { start, end };
  }, []);

  const getInitialDates = () => {
    return initialDates
  }

  const [startDate, setStartDate] = useState<Date | null>(initialDates.start);
  const [endDate, setEndDate] = useState<Date | null>(initialDates.end);

  const [deliveryNumber, setDeliveryNumber] = useState<string>('');

  const [mode, setMode] = useState<ReceivingMode | null>(null);

  const resetFilters = useCallback(() => {
    setDeliveryNumber('');
    setStartDate(initialDates.start);
    setEndDate(initialDates.end);
  }, []);

  const toggleFilter = useCallback((key: keyof ReceivingFilters) => {
    setFilters(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const activateDateMode = useCallback(() => {
    setMode(ReceivingMode.DATES);
    setDeliveryNumber('');
  }, []);

  const activateDeliveryMode = useCallback(() => {
    setMode(ReceivingMode.DELIVERY);
    setStartDate(null);
    setEndDate(null);
  }, []);

  const clearMode = useCallback(() => {
    setMode(null);
  }, []);

  const resetDates = useCallback(() => {
    setStartDate(initialDates.start);
    setEndDate(initialDates.end);
  }, []);

  const resetDelivery = useCallback(() => {
    setDeliveryNumber('');
  }, []);

  const setDeliverySearch = useCallback((value: string) => {
    if (value.length > 30) return;
    setDeliveryNumber(value);
  }, []);

  const setStartDateOnly = useCallback(
    (start: MaterialUiPickersDate | null) => {
      setStartDate(start);
    },
    []
  );

  const setEndDateOnly = useCallback((end: MaterialUiPickersDate | null) => {
    setEndDate(end);
  }, []);

  return {
    state: {
      filters,
      startDate,
      endDate,
      deliveryNumber,
      mode,
    },
    actions: {
      resetFilters,
      toggleFilter,
      activateDateMode,
      activateDeliveryMode,
      clearMode,
      resetDates,
      resetDelivery,
      setDeliverySearch,
      setStartDateOnly,
      setEndDateOnly,
      getInitialDates,
    },
  };
};
